import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Phone, Video } from 'lucide-react';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import Card from '../../components/UI/Card';

const ChatPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSupabaseAuth();
  const { sendNotification } = useNotifications();
  const [message, setMessage] = useState('');

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const messages = [
    { id: 1, text: "Hey! How are you?", sender: 'other', time: '10:30 AM' },
    { id: 2, text: "Hi! I'm doing great, thanks for asking! How about you?", sender: 'me', time: '10:32 AM' },
    { id: 3, text: "I'm good too! I saw you like photography, that's awesome!", sender: 'other', time: '10:35 AM' },
    { id: 4, text: "Yes! I love capturing moments. Do you have any hobbies?", sender: 'me', time: '10:37 AM' },
    { id: 5, text: "I love hiking and exploring new places. Maybe we could go on a photo walk sometime?", sender: 'other', time: '10:40 AM' }
  ];

  const handleSend = () => {
    if (message.trim()) {
      console.log('Sending message:', message);
      
      // Simulate receiving a reply after sending a message
      setTimeout(() => {
        sendNotification(
          'πPi Dating - New Message 💬',
          'Ella replied to your message!',
          'message'
        );
      }, 3000);
      
      setMessage('');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg p-4 flex items-center justify-between border-b border-purple-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/matches')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-800 dark:text-gray-200" />
          </button>
          <div className="flex items-center gap-3">
            <img
              src="https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=100"
              alt="Ella"
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <h2 className="font-semibold text-gray-800 dark:text-gray-100">Ella</h2>
              <p className="text-xs text-green-500">Online</p>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
            <Phone size={20} className="text-purple-600 dark:text-purple-400" />
          </button>
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
            <Video size={20} className="text-purple-600 dark:text-purple-400" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 space-y-4 overflow-y-auto bg-gray-50 dark:bg-gray-900">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                msg.sender === 'me'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200'
              }`}
            >
              <p>{msg.text}</p>
              <p className={`text-xs mt-1 ${
                msg.sender === 'me' ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'
              }`}>
                {msg.time}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Message Input */}
      <div className="p-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border-t border-purple-200 dark:border-gray-700">
        <div className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 rounded-full border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          />
          <button
            onClick={handleSend}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full hover:shadow-lg transition-all duration-200"
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;